import { FileSpreadsheet, FileUp, LoaderCircle, UploadCloud } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { CompletedLoadImportPanel } from "../components/imports/CompletedLoadImportPanel";
import { createResource, extractErrorMessage, fetchResourceList, uploadFormResource } from "../lib/api";

const duplicateStrategies = [
  { value: "skip", label: "Skip existing loads" },
  { value: "update", label: "Update existing loads" },
  { value: "duplicate", label: "Create duplicate copies" },
];

const SummaryCard = ({ label, value, testId }) => (
  <div className="rounded-3xl border border-zinc-200 bg-zinc-50 px-5 py-4" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{label}</p>
    <p className="mt-2 text-3xl font-semibold text-zinc-900">{value}</p>
  </div>
);

export default function LoadImportPage() {
  const [importMode, setImportMode] = useState("standard");
  const [recentLogs, setRecentLogs] = useState([]);
  const [previewSession, setPreviewSession] = useState(null);
  const [mapping, setMapping] = useState({});
  const [duplicateStrategy, setDuplicateStrategy] = useState("skip");
  const [validation, setValidation] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [busy, setBusy] = useState({ upload: false, validate: false, import: false });
  const [dragActive, setDragActive] = useState(false);

  const loadLogs = async () => {
    try {
      const payload = await fetchResourceList("/loads/import/logs");
      setRecentLogs(payload);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to load import history."));
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    try {
      setBusy((current) => ({ ...current, upload: true }));
      const formData = new FormData();
      formData.append("file", file);
      const payload = await uploadFormResource("/loads/import/preview", formData);
      setPreviewSession(payload);
      setMapping(payload.suggested_mapping || {});
      setValidation(null);
      setImportResult(null);
      toast.success(`Loaded ${payload.total_rows} CSV rows for mapping.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to read that CSV file."));
    } finally {
      setBusy((current) => ({ ...current, upload: false }));
    }
  };

  const validateImport = async () => {
    if (!previewSession) return;
    try {
      setBusy((current) => ({ ...current, validate: true }));
      const payload = await createResource("/loads/import/validate", {
        import_log_id: previewSession.import_log_id,
        field_mapping: mapping,
        duplicate_strategy: duplicateStrategy,
      });
      setValidation(payload);
      setImportResult(null);
      toast.success("Preview refreshed. Review row issues before importing.");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to validate the CSV rows."));
    } finally {
      setBusy((current) => ({ ...current, validate: false }));
    }
  };

  const commitImport = async () => {
    if (!previewSession) return;
    try {
      setBusy((current) => ({ ...current, import: true }));
      const payload = await createResource("/loads/import/commit", {
        import_log_id: previewSession.import_log_id,
        field_mapping: mapping,
        duplicate_strategy: duplicateStrategy,
      });
      setImportResult(payload);
      toast.success(`Imported ${payload.summary.imported_rows} load rows.`);
      loadLogs();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to complete the CSV import."));
    } finally {
      setBusy((current) => ({ ...current, import: false }));
    }
  };

  const previewRows = validation?.rows || [];
  const previewTableRows = useMemo(() => previewRows.slice(0, 25), [previewRows]);

  const modeToggle = (
    <div className="inline-flex flex-wrap gap-2 rounded-full border border-zinc-200 bg-white p-1" data-testid="load-import-mode-toggle">
      <button className={`rounded-full px-4 py-2 text-sm font-semibold ${importMode === "standard" ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid="load-import-mode-standard" onClick={() => setImportMode("standard")} type="button">Standard load rows</button>
      <button className={`rounded-full px-4 py-2 text-sm font-semibold ${importMode === "completed" ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid="load-import-mode-completed" onClick={() => setImportMode("completed")} type="button">Completed / multi-leg</button>
    </div>
  );

  if (importMode === "completed") {
    return (
      <div className="space-y-6" data-testid="load-import-page">
        <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Dashboard import wizard</p>
            <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Import loads from CSV</h1>
          </div>
          <div className="flex flex-wrap gap-3">{modeToggle}</div>
        </section>
        <CompletedLoadImportPanel />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="load-import-page">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Dashboard import wizard</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Import loads from CSV</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          {modeToggle}
          <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="load-import-template-link" href="/load-import-template.csv?v=customer-ref">
            <FileSpreadsheet className="h-4 w-4" /> Download sample CSV
          </a>
          <Link className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800" data-testid="load-import-dispatch-link" to="/dispatch-board">
            <FileUp className="h-4 w-4" /> View dispatch board
          </Link>
        </div>
      </section>

      <section
        className={`rounded-[32px] border-2 border-dashed px-6 py-10 text-center transition ${dragActive ? "border-zinc-900 bg-zinc-50" : "border-zinc-300 bg-white"}`}
        data-testid="load-import-dropzone"
        onDragEnter={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragActive(false);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          handleUpload(event.dataTransfer.files?.[0]);
        }}
      >
        <UploadCloud className="mx-auto h-10 w-10 text-zinc-400" />
        <h2 className="mt-4 text-2xl font-semibold text-zinc-900">Drag and drop your load CSV</h2>
        <label className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="load-import-upload-button">
          {busy.upload ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
          Choose CSV file
          <input accept=".csv" className="hidden" onChange={(event) => handleUpload(event.target.files?.[0])} type="file" />
        </label>
      </section>

      {previewSession ? (
        <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="table-shell">
            <div className="border-b border-zinc-200 px-5 py-4">
              <h2 className="text-2xl font-semibold text-zinc-900">Field mapping</h2>
            </div>
            <div className="overflow-x-auto px-5 py-5">
              <table className="data-table" data-testid="load-import-mapping-table">
                <thead>
                  <tr>
                    <th>TMS field</th>
                    <th>CSV column</th>
                  </tr>
                </thead>
                <tbody>
                  {previewSession.field_options.map((field) => (
                    <tr key={field.key}>
                      <td>
                        <p className="font-medium text-zinc-900">{field.label}</p>
                        <p className="mt-1 text-xs text-zinc-500">{field.required ? "Required" : "Optional"}</p>
                      </td>
                      <td>
                        <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid={`load-import-mapping-${field.key}`} onChange={(event) => setMapping((current) => ({ ...current, [field.key]: event.target.value || null }))} value={mapping[field.key] || ""}>
                          <option value="">Ignore for now</option>
                          {previewSession.headers.map((header) => <option key={`${field.key}-${header}`} value={header}>{header}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-6">
            <div className="panel-surface px-5 py-5" data-testid="load-import-options-card">
              <h2 className="text-2xl font-semibold text-zinc-900">Import options</h2>
              <div className="mt-4 space-y-3">
                {duplicateStrategies.map((option) => (
                  <label className="flex items-start gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-4" key={option.value}>
                    <input checked={duplicateStrategy === option.value} data-testid={`load-import-duplicate-${option.value}`} name="duplicateStrategy" onChange={() => setDuplicateStrategy(option.value)} type="radio" />
                    <span className="text-sm text-zinc-700">{option.label}</span>
                  </label>
                ))}
              </div>
              <button className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="load-import-validate-button" onClick={validateImport} type="button">
                {busy.validate ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                Validate preview
              </button>
            </div>

            <div className="panel-surface px-5 py-5" data-testid="load-import-raw-preview-card">
              <h2 className="text-2xl font-semibold text-zinc-900">CSV preview</h2>
              <p className="mt-1 text-sm text-zinc-500">Showing the first {previewSession.sample_rows.length} rows from {previewSession.total_rows} total CSV rows.</p>
              <div className="mt-4 overflow-x-auto">
                <table className="data-table text-xs" data-testid="load-import-raw-preview-table">
                  <thead>
                    <tr>
                      {previewSession.headers.map((header) => <th key={header}>{header}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {previewSession.sample_rows.map((row, index) => (
                      <tr key={`sample-${index}`}>
                        {previewSession.headers.map((header) => <td key={`${index}-${header}`}>{row[header] || "—"}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {validation ? (
        <section className="space-y-6" data-testid="load-import-validation-section">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Total rows" testId="load-import-total-rows" value={validation.summary.total_rows} />
            <SummaryCard label="Ready to import" testId="load-import-importable-rows" value={validation.summary.importable_rows} />
            <SummaryCard label="Skipped" testId="load-import-skipped-rows" value={validation.summary.skipped_rows} />
            <SummaryCard label="Failed" testId="load-import-failed-rows" value={validation.summary.failed_rows} />
          </div>

          <div className="table-shell">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
              <div>
                <h2 className="text-2xl font-semibold text-zinc-900">Validated row preview</h2>
                <p className="mt-1 text-sm text-zinc-500">Rows with issues stay visible here so you can fix the CSV before importing again.</p>
              </div>
              <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-zinc-400" data-testid="load-import-commit-button" disabled={!validation.summary.importable_rows || busy.import} onClick={commitImport} type="button">
                {busy.import ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                Import successful rows
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="load-import-validation-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Load</th>
                    <th>Customer</th>
                    <th>Customer Ref</th>
                    <th>Pickup</th>
                    <th>Delivery</th>
                    <th>Status</th>
                    <th>Duplicate</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {previewTableRows.map((row) => (
                    <tr key={`validated-${row.row_number}`}>
                      <td>{row.row_number}</td>
                      <td>{row.load_number || "—"}</td>
                      <td>{row.customer_name || "—"}</td>
                      <td>{row.customer_reference_number || "—"}</td>
                      <td>
                        <p>{row.pickup_company || "—"}</p>
                        <p className="mt-1 text-xs text-zinc-500">{row.pickup_datetime || "—"}</p>
                      </td>
                      <td>
                        <p>{row.delivery_company || "—"}</p>
                        <p className="mt-1 text-xs text-zinc-500">{row.delivery_datetime || "—"}</p>
                      </td>
                      <td>{row.status || "—"}</td>
                      <td>{row.duplicate_action || "New load"}</td>
                      <td>
                        {row.errors.length ? row.errors.map((error) => <p className="text-xs text-red-600" key={`${row.row_number}-${error}`}>{error}</p>) : <span className="text-xs font-semibold text-emerald-600">Ready</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {importResult ? (
        <section className="panel-surface px-5 py-5" data-testid="load-import-result-card">
          <h2 className="text-2xl font-semibold text-zinc-900">Import summary</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Total rows" testId="load-import-result-total" value={importResult.summary.total_rows} />
            <SummaryCard label="Imported rows" testId="load-import-result-imported" value={importResult.summary.imported_rows} />
            <SummaryCard label="Skipped rows" testId="load-import-result-skipped" value={importResult.summary.skipped_rows} />
            <SummaryCard label="Failed rows" testId="load-import-result-failed" value={importResult.summary.failed_rows} />
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="load-import-go-dispatch" to="/dispatch-board">
              <FileUp className="h-4 w-4" /> Open dispatch board
            </Link>
            <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700" data-testid="load-import-start-over" onClick={() => { setPreviewSession(null); setValidation(null); setImportResult(null); setMapping({}); }} type="button">
              Start another import
            </button>
          </div>
        </section>
      ) : null}

      <section className="table-shell" data-testid="load-import-history-section">
        <div className="border-b border-zinc-200 px-5 py-4">
          <h2 className="text-2xl font-semibold text-zinc-900">Recent import logs</h2>
          <p className="mt-1 text-sm text-zinc-500">See when CSV loads were imported, by whom, and what happened.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="load-import-history-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Imported by</th>
                <th>Status</th>
                <th>Total</th>
                <th>Imported</th>
                <th>Skipped</th>
                <th>Failed</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {recentLogs.length ? recentLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.file_name}</td>
                  <td>{log.created_by || "—"}</td>
                  <td>{log.import_status}</td>
                  <td>{log.summary?.total_rows || 0}</td>
                  <td>{log.summary?.imported_rows || log.summary?.importable_rows || 0}</td>
                  <td>{log.summary?.skipped_rows || 0}</td>
                  <td>{log.summary?.failed_rows || 0}</td>
                  <td>{log.created_at || "—"}</td>
                </tr>
              )) : (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={8}>No CSV imports have been run yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}